import { Link } from "react-router-dom";
import { ArrowRight, Users, Server, BarChart3, ShieldCheck, Brain } from "lucide-react";

const services = [
  {
    icon: Users,
    title: "CIO as a Service",
    subtitle: "Strategic IT Leadership, On Demand",
    desc: "Executive-level IT leadership without the cost and complexity of a full-time CIO. Our experts deliver strategic direction, governance, and digital transformation guidance tailored to your business objectives.",
    highlights: [
      "IT Strategy & Digital Transformation",
      "IT Governance & Risk Management",
      "Business-IT Alignment",
      "Cost Optimization & Sourcing",
    ],
    path: "/services/cio",
  },
  {
    icon: Server,
    title: "IT Infrastructure Management",
    subtitle: "Agility, Performance, and Always On Support",
    desc: "Modern enterprises need resilient, secure, and flexible infrastructure for hybrid workloads, distributed teams, and mission-critical operations. We deliver 24x7 monitoring, proactive issue prevention, and seamless scalability.",
    highlights: [
      "Managed OS & Database Services",
      "Cloud Infrastructure (AWS, Azure, GCP)",
      "Infrastructure Monitoring & NOC",
      "Compliance & Audit Support",
    ],
    path: "/services/infrastructure",
  },
  {
    icon: BarChart3,
    title: "ERP Operations & Management",
    subtitle: "Integrated Products for Enterprise Execution",
    desc: "We deliver ERP and ERP-integrated products that extend workflows into real-world execution environments without modifying the ERP core, ensuring scalability, compliance, and innovation.",
    highlights: [
      "ERP Implementation & Migration",
      "Seamless ERP Integration (SAP, Oracle, Dynamics)",
      "Mobile-first Execution Layer",
      "Governance & Compliance",
    ],
    path: "/services/erp",
  },
  {
    icon: ShieldCheck,
    title: "Cybersecurity & Compliance",
    subtitle: "Safeguard Your Enterprise, Ensure Compliance",
    desc: "End-to-end cybersecurity and compliance services that protect your data, secure your operations, and ensure audit readiness across global regulatory frameworks.",
    highlights: [
      "Security Strategy & Assessment",
      "Data Protection & Privacy (GDPR, HIPAA, SOX)",
      "24x7 Security Operations Center (SOC)",
      "Identity & Access Management (IAM)",
    ],
    path: "/services/cybersecurity",
  },
  {
    icon: Brain,
    title: "Artificial Intelligence (AI) Services",
    subtitle: "Enterprise Grade AI for Intelligent Decision Making",
    desc: "Enterprise ready AI solutions designed to enhance operational efficiency, decision making, and digital innovation. We combine data intelligence, machine learning, automation, and enterprise integration.",
    highlights: [
      "AI Strategy & Consulting",
      "Machine Learning & Predictive Analytics",
      "NLP & Intelligent Chatbots",
      "AI Integration with Enterprise Applications",
    ],
    path: "/services/ai",
  },
];

export default function ServicesPage() {
  return (
    <div data-testid="services-page">
      {/* Hero */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-4">
              Our Services
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none text-[#0A0A0A] mb-8">
              Enterprise Technology Solutions
            </h1>
            <p className="text-lg text-[#4B5563] leading-relaxed">
              From strategic IT leadership to infrastructure management, ERP operations,
              cybersecurity, and AI solutions, we deliver comprehensive technology solutions
              that drive measurable business outcomes.
            </p>
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="pb-20 lg:pb-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="space-y-0 border border-gray-200">
            {services.map((service, i) => (
              <div
                key={service.title}
                data-testid={`services-list-item-${i}`}
                className={`grid grid-cols-1 lg:grid-cols-2 ${
                  i < services.length - 1 ? "border-b border-gray-200" : ""
                }`}
              >
                <div className="p-8 lg:p-12 lg:border-r border-gray-200">
                  <service.icon className="w-8 h-8 text-[#002FA7] mb-6" />
                  <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-[#0A0A0A] mb-2">
                    {service.title}
                  </h2>
                  <p className="text-sm font-medium text-[#002FA7] mb-4">{service.subtitle}</p>
                  <p className="text-sm text-[#4B5563] leading-relaxed">{service.desc}</p>
                </div>
                <div className="p-8 lg:p-12 bg-[#F3F4F6] flex flex-col justify-between">
                  <div>
                    <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#4B5563] mb-4">
                      Key Capabilities
                    </p>
                    <ul className="space-y-3 mb-8">
                      {service.highlights.map((h) => (
                        <li key={h} className="flex items-start gap-3 text-sm text-[#0A0A0A]">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#002FA7] mt-1.5 shrink-0" />
                          {h}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <Link
                    to={service.path}
                    data-testid={`service-detail-link-${i}`}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-[#002FA7] hover:text-[#001F70] transition-colors"
                  >
                    View Details <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-[#F3F4F6]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-none text-[#0A0A0A] mb-6">
            Not sure which service fits your needs?
          </h2>
          <p className="text-base text-[#4B5563] max-w-lg mx-auto mb-10">
            Our team of experts will assess your current landscape and recommend
            the right combination of services to drive your business forward.
          </p>
          <Link
            to="/contact"
            data-testid="services-cta-link"
            className="inline-flex items-center gap-2 px-10 py-4 bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#001F70] transition-colors"
          >
            Talk to an Expert <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
